import * as React from "react";
import { useMemo, useCallback, useRef } from "react";
import { Field } from "formik";
import moment from "moment";
import styled, { css } from "styled-components";
import ViewsRefreshControls from "views/components/searchbar/ViewsRefreshControls";
import { Spinner } from "components/common";
import ScrollToHint from "views/components/common/ScrollToHint";
import SearchButton from "views/components/searchbar/SearchButton";
import ViewsQueryInput from "views/components/searchbar/ViewsQueryInput";
import DashboardActionsMenu from "views/components/DashboardActionsMenu";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import ViewsQueryValidation from "views/components/searchbar/queryvalidation/ViewsQueryValidation";
import FormWarningsContext from "contexts/FormWarningsContext";
import FormWarningsProvider from "contexts/FormWarningsProvider";
import useParameters from "views/hooks/useParameters";
import debounceWithPromise from "views/logic/debounceWithPromise";
import validateQuery from "views/components/searchbar/queryvalidation/validateQuery";
import { isNoTimeRangeOverride } from "views/typeGuards/timeRange";
import ValidateOnParameterChange from "views/components/searchbar/ValidateOnParameterChange";
import {
  executeSearchSubmitHandler as executePluggableSubmitHandler,
  useInitialSearchValues as usePluggableInitialValues,
  pluggableValidationPayload
} from "views/logic/searchbar/pluggableSearchBarControlsHandler";
import usePluginEntities from "hooks/usePluginEntities";
import useUserDateTime from "hooks/useUserDateTime";
import {
  SEARCH_BAR_GAP,
  SEARCH_BAR_CONTAINER_NAME,
  SearchBarContainer,
  SearchQueryRow,
  SearchButtonAndQuery,
  SearchInputAndValidationContainer
} from "views/components/searchbar/SearchBarLayout";
import PluggableCommands from "views/components/searchbar/queryinput/PluggableCommands";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { setGlobalOverride } from "views/logic/slices/searchExecutionSlice";
import useGlobalOverride from "views/hooks/useGlobalOverride";
import useHandlerContext from "views/components/useHandlerContext";
import QueryHistoryButton from "views/components/searchbar/QueryHistoryButton";
import useView from "views/hooks/useView";
import useIsLoading from "views/hooks/useIsLoading";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import { executeActiveQuery } from "views/logic/slices/viewSlice";
import useViewsSelector from "views/stores/useViewsSelector";
import { selectCurrentQueryResults } from "views/logic/slices/viewSelectors";
import { NO_TIMERANGE_OVERRIDE } from "views/Constants";
import TimeRangeFilter from "./searchbar/time-range-filter";
import DashboardSearchForm from "./DashboardSearchBarForm";
import PluggableSearchBarControls from "./searchbar/PluggableSearchBarControls";
const TimeRangeRow = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    gap: ${SEARCH_BAR_GAP};

    @container ${SEARCH_BAR_CONTAINER_NAME} (max-width: ${theme.breakpoints.max.sm}) {
      flex-direction: column;
    }
  `
);
const StyledTimeRangeFilter = styled(TimeRangeFilter)(
  ({ theme }) => css`
    flex: 0.2;
    flex-basis: 380px;

    @container ${SEARCH_BAR_CONTAINER_NAME} (max-width: ${theme.breakpoints.max.sm}) {
      flex: 1;
      flex-basis: auto;
    }
  `
);
const debouncedValidateQuery = debounceWithPromise(validateQuery, 350);
const _validateQueryString = (values, pluggableSearchBarControls, userTimezone, context) => {
  const request = {
    timeRange: isNoTimeRangeOverride(values == null ? void 0 : values.timerange) ? void 0 : values == null ? void 0 : values.timerange,
    queryString: values == null ? void 0 : values.queryString,
    ...pluggableValidationPayload(values, context, pluggableSearchBarControls)
  };
  return debouncedValidateQuery(request, userTimezone);
};
const useInitialFormValues = (timerange, queryString) => {
  const initialValuesFromPlugins = usePluggableInitialValues();
  return useMemo(
    () => ({ timerange, queryString, ...initialValuesFromPlugins }),
    [queryString, timerange, initialValuesFromPlugins]
  );
};
const DashboardSearchBar = ({ scrollContainer }) => {
  var _a, _b;
  const editorRef = useRef(null);
  const view = useView();
  const { userTimezone } = useUserDateTime();
  const { config } = useSearchConfiguration();
  const results = useViewsSelector(selectCurrentQueryResults);
  const { timerange, query: { query_string: queryString = "" } = {} } = (_a = useGlobalOverride()) != null ? _a : {};
  const pluggableSearchBarControls = usePluginEntities("views.components.searchBar");
  const dispatch = useViewsDispatch();
  const handlerContext = useHandlerContext();
  const { restartAutoRefresh } = useAutoRefresh();
  const submitForm = useCallback(
    async (values) => {
      const { timerange: newTimerange, queryString: newQueryString } = values;
      restartAutoRefresh();
      await executePluggableSubmitHandler(dispatch, values, pluggableSearchBarControls);
      dispatch(setGlobalOverride(newQueryString, newTimerange));
      dispatch(executeActiveQuery());
    },
    [dispatch, pluggableSearchBarControls, restartAutoRefresh]
  );
  const { parameters } = useParameters();
  const initialValues = useInitialFormValues(timerange, queryString);
  const isLoadingExecution = useIsLoading();
  if (!config) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const limitDuration = (_b = moment.duration(config.query_time_range_limit).asSeconds()) != null ? _b : 0;
  return /* @__PURE__ */ React.createElement(WidgetFocusContext.Consumer, null, ({ focusedWidget: { editing } = { editing: false } }) => /* @__PURE__ */ React.createElement(FormWarningsProvider, null, /* @__PURE__ */ React.createElement(
    DashboardSearchForm,
    {
      initialValues,
      limitDuration,
      onSubmit: submitForm,
      validateQueryString: (values) => _validateQueryString(values, pluggableSearchBarControls, userTimezone, handlerContext)
    },
    ({
      dirty,
      errors,
      isSubmitting,
      isValid,
      isValidating,
      handleSubmit,
      values,
      setFieldValue,
      validateForm
    }) => {
      const disableSearchSubmit = isSubmitting || isValidating || !isValid || isLoadingExecution;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        ScrollToHint,
        {
          ifValueChanges: queryString,
          scrollContainer,
          title: "Scroll to search bar"
        }
      ), /* @__PURE__ */ React.createElement(SearchBarContainer, null, /* @__PURE__ */ React.createElement(ValidateOnParameterChange, { parameters }), /* @__PURE__ */ React.createElement(TimeRangeRow, null, /* @__PURE__ */ React.createElement(
        StyledTimeRangeFilter,
        {
          onChange: (nextTimeRange) => setFieldValue("timerange", nextTimeRange),
          value: values == null ? void 0 : values.timerange,
          limitDuration,
          hasErrorOnMount: !!errors.timerange,
          moveRangeProps: {
            effectiveTimerange: results == null ? void 0 : results.effectiveTimerange,
            initialTimerange: timerange != null ? timerange : NO_TIMERANGE_OVERRIDE,
            initialTimerangeFormat: "internal"
          },
          noOverride: true
        }
      ), /* @__PURE__ */ React.createElement(ViewsRefreshControls, { disable: !isValid })), /* @__PURE__ */ React.createElement(SearchQueryRow, null, /* @__PURE__ */ React.createElement(SearchButtonAndQuery, null, /* @__PURE__ */ React.createElement(
        SearchButton,
        {
          disabled: disableSearchSubmit,
          glyph: "filter_alt",
          displaySpinner: isSubmitting || isLoadingExecution,
          dirty
        }
      ), /* @__PURE__ */ React.createElement(SearchInputAndValidationContainer, null, /* @__PURE__ */ React.createElement(Field, { name: "queryString" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(FormWarningsContext.Consumer, null, ({ warnings }) => /* @__PURE__ */ React.createElement(PluggableCommands, { usage: "global_override_query" }, (customCommands) => /* @__PURE__ */ React.createElement(
        ViewsQueryInput,
        {
          value,
          view,
          timeRange: values == null ? void 0 : values.timerange,
          placeholder: "Apply filter to all widgets",
          name,
          onChange,
          disableExecution: disableSearchSubmit,
          error,
          isValidating,
          validate: validateForm,
          warning: warnings.queryString,
          ref: editorRef,
          onExecute: handleSubmit,
          commands: customCommands
        }
      )))), /* @__PURE__ */ React.createElement(ViewsQueryValidation, null), /* @__PURE__ */ React.createElement(QueryHistoryButton, { editorRef }))), !editing && /* @__PURE__ */ React.createElement(DashboardActionsMenu, null)), /* @__PURE__ */ React.createElement(PluggableSearchBarControls, { showLeftControls: false })));
    }
  )));
};
export default DashboardSearchBar;
